// src/components/SmartFlow/ExecutionLogsViewer.jsx
import React, { useState, useEffect } from "react";
import { api } from "../../services/api";

/**
 * Execution Logs Viewer
 *
 * Displays detailed execution logs for a workflow execution
 * Features:
 * - Timeline view of all execution steps
 * - Filtering by log level (INFO, WARNING, ERROR)
 * - Filtering by node
 * - Performance metrics (duration)
 * - Drill-down into each node's execution
 *
 * Phase 3.3 implementation
 */
const ExecutionLogsViewer = ({ executionId, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterLevel, setFilterLevel] = useState("ALL");
  const [filterNode, setFilterNode] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // Expanded logs (for drill-down)
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  useEffect(() => {
    if (executionId) {
      fetchExecutionLogs();
    }
  }, [executionId]);

  const fetchExecutionLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch execution details
      const execResponse = await api.get(`/api/workflows/executions/${executionId}`);
      setExecution(execResponse.data);

      // Fetch logs
      const logsResponse = await api.get(`/api/workflows/executions/${executionId}/logs`);
      setLogs(logsResponse.data.logs || []);
    } catch (err) {
      console.error("Error fetching execution logs:", err);
      setError("Failed to load execution logs");
    } finally {
      setLoading(false);
    }
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  // Get unique nodes from logs
  const uniqueNodes = [...new Set(logs.map(log => log.node_id).filter(Boolean))];

  // Filter logs
  const filteredLogs = logs.filter(log => {
    // Level filter
    if (filterLevel !== "ALL" && log.level !== filterLevel) {
      return false;
    }

    // Node filter
    if (filterNode !== "ALL" && log.node_id !== filterNode) {
      return false;
    }

    // Search query
    if (searchQuery && !log.message.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return true;
  });

  // Calculate stats
  const stats = {
    total: logs.length,
    info: logs.filter(l => l.level === 'INFO').length,
    warning: logs.filter(l => l.level === 'WARNING').length,
    error: logs.filter(l => l.level === 'ERROR').length,
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'INFO':
        return '#3498db';
      case 'WARNING':
        return '#f39c12';
      case 'ERROR':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'INFO':
        return 'ℹ️';
      case 'WARNING':
        return '⚠️';
      case 'ERROR':
        return '❌';
      default:
        return '•';
    }
  };

  const getStatusBadgeStyle = (status) => {
    let backgroundColor;
    switch (status) {
      case 'COMPLETED':
        backgroundColor = '#27ae60';
        break;
      case 'FAILED':
        backgroundColor = '#e74c3c';
        break;
      case 'RUNNING':
        backgroundColor = '#f39c12';
        break;
      default:
        backgroundColor = '#95a5a6';
    }

    return {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      color: 'white',
      backgroundColor,
    };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ padding: "40px", textAlign: "center", color: "#7f8c8d" }}>
          <div style={{ marginBottom: "10px", fontSize: "18px" }}>Loading logs...</div>
          <div style={{ fontSize: "12px" }}>Fetching execution logs from API</div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div style={{ color: "#e74c3c", marginBottom: "10px", fontSize: "16px" }}>{error}</div>
          <button
            onClick={fetchExecutionLogs}
            style={{
              padding: "8px 16px",
              background: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div>
        {/* Execution Summary */}
        {execution && (
          <div style={{
            padding: "16px",
            background: "#f8f9fa",
            borderRadius: "8px",
            marginBottom: "20px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px" }}>Execution Summary</h3>
                <div style={{ fontSize: "12px", color: "#7f8c8d", marginTop: "4px" }}>
                  ID: {execution.id}
                </div>
              </div>
              <span style={getStatusBadgeStyle(execution.status)}>{execution.status}</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", fontSize: "13px" }}>
              <div>
                <div style={{ color: "#7f8c8d" }}>Started</div>
                <div style={{ fontWeight: "bold" }}>{formatTimestamp(execution.started_at)}</div>
              </div>
              <div>
                <div style={{ color: "#7f8c8d" }}>Completed</div>
                <div style={{ fontWeight: "bold" }}>
                  {execution.completed_at ? formatTimestamp(execution.completed_at) : 'Running...'}
                </div>
              </div>
              <div>
                <div style={{ color: "#7f8c8d" }}>Duration</div>
                <div style={{ fontWeight: "bold" }}>
                  {execution.completed_at && execution.started_at
                    ? formatDuration(new Date(execution.completed_at) - new Date(execution.started_at))
                    : 'Running...'}
                </div>
              </div>
            </div>

            {execution.error_message && (
              <div style={{
                marginTop: "12px",
                padding: "10px",
                background: "#fee",
                border: "1px solid #e74c3c",
                borderRadius: "4px",
                color: "#c0392b",
                fontSize: "13px"
              }}>
                <strong>Error:</strong> {execution.error_message}
              </div>
            )}
          </div>
        )}

        {/* Statistics */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
          marginBottom: "20px"
        }}>
          <div style={{
            padding: "12px",
            background: "#ecf0f1",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#34495e" }}>{stats.total}</div>
            <div style={{ fontSize: "12px", color: "#7f8c8d" }}>Total Logs</div>
          </div>
          <div style={{
            padding: "12px",
            background: "#e3f2fd",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3498db" }}>{stats.info}</div>
            <div style={{ fontSize: "12px", color: "#7f8c8d" }}>Info</div>
          </div>
          <div style={{
            padding: "12px",
            background: "#fff3e0",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#f39c12" }}>{stats.warning}</div>
            <div style={{ fontSize: "12px", color: "#7f8c8d" }}>Warnings</div>
          </div>
          <div style={{
            padding: "12px",
            background: "#ffebee",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#e74c3c" }}>{stats.error}</div>
            <div style={{ fontSize: "12px", color: "#7f8c8d" }}>Errors</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: "flex",
          gap: "12px",
          marginBottom: "20px",
          flexWrap: "wrap"
        }}>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            />
          </div>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
              background: "white"
            }}
          >
            <option value="ALL">All Levels</option>
            <option value="INFO">Info</option>
            <option value="WARNING">Warning</option>
            <option value="ERROR">Error</option>
          </select>
          <select
            value={filterNode}
            onChange={(e) => setFilterNode(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
              background: "white"
            }}
          >
            <option value="ALL">All Nodes</option>
            {uniqueNodes.map(nodeId => (
              <option key={nodeId} value={nodeId}>
                Node {nodeId.substring(0, 8)}
              </option>
            ))}
          </select>
        </div>

        {/* Logs Timeline */}
        <div style={{
          background: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          maxHeight: "500px",
          overflowY: "auto"
        }}>
          {filteredLogs.length === 0 ? (
            <div style={{
              padding: "40px",
              textAlign: "center",
              color: "#7f8c8d"
            }}>
              {logs.length === 0 ? 'No logs found' : 'No logs match the current filters'}
            </div>
          ) : (
            filteredLogs.map((log, index) => {
              const isExpanded = expandedLogs.has(log.id);
              const hasContextData = log.context_data && Object.keys(log.context_data).length > 0;

              return (
                <div
                  key={log.id || index}
                  style={{
                    borderBottom: index < filteredLogs.length - 1 ? "1px solid #e0e0e0" : "none",
                    padding: "12px 16px",
                    cursor: hasContextData ? "pointer" : "default",
                    transition: "background 0.2s",
                  }}
                  onClick={() => hasContextData && toggleLogExpansion(log.id)}
                  onMouseEnter={(e) => hasContextData && (e.currentTarget.style.background = "#f8f9fa")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    {/* Level Icon */}
                    <div style={{
                      fontSize: "20px",
                      minWidth: "20px",
                      paddingTop: "2px"
                    }}>
                      {getLevelIcon(log.level)}
                    </div>

                    {/* Log Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <span style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: getLevelColor(log.level)
                          }}>
                            {log.level}
                          </span>
                          {log.node_id && (
                            <span style={{
                              fontSize: "11px",
                              color: "#7f8c8d",
                              fontFamily: "monospace"
                            }}>
                              Node: {log.node_id.substring(0, 8)}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "#7f8c8d" }}>
                          {log.duration_ms && (
                            <span>⏱️ {formatDuration(log.duration_ms)}</span>
                          )}
                          <span>{formatTimestamp(log.timestamp)}</span>
                        </div>
                      </div>

                      <div style={{
                        fontSize: "14px",
                        color: "#2c3e50",
                        wordBreak: "break-word"
                      }}>
                        {log.message}
                      </div>

                      {/* Expanded Context Data */}
                      {isExpanded && hasContextData && (
                        <div style={{
                          marginTop: "12px",
                          padding: "12px",
                          background: "#f8f9fa",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          overflowX: "auto"
                        }}>
                          <div style={{ marginBottom: "6px", fontWeight: "bold", color: "#7f8c8d" }}>
                            Context Data:
                          </div>
                          <pre style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all"
                          }}>
                            {JSON.stringify(log.context_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Expand indicator */}
                    {hasContextData && (
                      <div style={{
                        fontSize: "12px",
                        color: "#7f8c8d",
                        minWidth: "20px",
                        paddingTop: "2px"
                      }}>
                        {isExpanded ? '▼' : '▶'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          width: "95%",
          maxWidth: "1200px",
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "20px" }}>Execution Logs</h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              color: "#7f8c8d",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content - Scrollable */}
        <div style={{
          padding: "20px",
          flex: 1,
          overflowY: "auto"
        }}>
          {renderContent()}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "15px 20px",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "12px", color: "#7f8c8d" }}>
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              background: "#95a5a6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionLogsViewer;
